/**
 * V-key walk/run state indicator. Renders top-right of HUD.
 * Shows current mode + key hint + stamina note so new players discover the toggle.
 *
 * Reads `me.survival.walkToggled` + the `Exhausted` transient per frame (Colyseus
 * may leave `survival`/`meta` undefined on the first delta — null-guard via `?.`).
 * Exhausted (0 stamina → walk-only) takes priority so the icon never claims
 * "Running" while the player is actually slowed.
 */
import type { Cube } from "@tribez/shared";

let el: HTMLDivElement | null = null;

function ensureEl(): HTMLDivElement {
  if (el) return el;
  el = document.createElement("div");
  el.id = "walk-state-icon";
  Object.assign(el.style, {
    position: "fixed",
    top: "16px",
    right: "16px",
    fontSize: "13px",
    lineHeight: "1.3",
    background: "rgba(0,0,0,0.55)",
    padding: "5px 9px",
    borderRadius: "6px",
    color: "rgba(255,255,255,0.9)",
    fontFamily: "sans-serif",
    zIndex: "10",
    pointerEvents: "none",
    textAlign: "right",
    userSelect: "none",
  } as CSSStyleDeclaration);
  document.body.appendChild(el);
  return el;
}

export function renderWalkStateIcon(me: Cube | undefined): void {
  if (!me) return;
  const icon = ensureEl();
  const exhausted = me.meta?.behaviors?.has("Exhausted") ?? false;
  const walking = me.survival?.walkToggled ?? false;

  // Rebuild children only when state changes (avoids thrashing the DOM every frame).
  const nextKey = exhausted ? "exhausted" : walking ? "walk" : "run";
  if (icon.dataset["mode"] === nextKey) return;
  icon.dataset["mode"] = nextKey;

  icon.replaceChildren();
  const labelText = exhausted ? "😮‍💨 Exhausted  " : walking ? "🚶 Walking  " : "🏃 Running  ";
  const label = document.createTextNode(labelText);
  const hint = document.createElement("span");
  hint.style.opacity = "0.6";
  hint.style.fontSize = "11px";
  hint.textContent = exhausted
    ? "out of stamina · walk only until you recover"
    : walking ? "[V] to run" : "[V] to walk · saves stamina";
  icon.appendChild(label);
  icon.appendChild(hint);
}
